import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutGrid, Menu, X } from "lucide-react";
import { EASE } from "./Reveal";

const LINKS = [
    { label: "Features", href: "#features", testid: "nav-link-features" },
    { label: "Chatbot", href: "#chatbot", testid: "nav-link-chatbot" },
    { label: "Floor Plans", href: "#floor-plans", testid: "nav-link-floor-plans" },
    { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export function scrollToId(e, href, after) {
    e.preventDefault();
    if (after) after();
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.2 });
    else el.scrollIntoView({ behavior: "smooth" });
}

export function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
                scrolled
                    ? "bg-[var(--paper)]/85 backdrop-blur-md border-b border-[var(--line)] shadow-[0_2px_16px_-8px_rgba(15,23,42,0.12)]"
                    : "bg-[var(--paper)]/40 backdrop-blur-sm border-b border-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <a
                    href="#top"
                    onClick={(e) => scrollToId(e, "#top")}
                    data-testid="nav-logo-link"
                    className="flex items-center gap-2.5 min-h-[44px] group"
                >
                    <span className="w-8 h-8 rounded-md bg-[var(--accent)] text-[#0A0908] grid place-items-center transition-transform duration-300 group-hover:rotate-90">
                        <LayoutGrid size={17} strokeWidth={2.2} />
                    </span>
                    <span className="font-display font-extrabold text-lg tracking-tight text-[var(--ink)]">
                        IDeaSpace
                    </span>
                    <span className="mono hidden md:inline-block text-[10px] tracking-[0.2em] text-[var(--ink-3)] border border-[var(--line)] rounded px-1.5 py-0.5">
                        IDeaS · PUNE
                    </span>
                </a>

                <div className="hidden md:flex items-center gap-1">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={l.testid}
                            onClick={(e) => scrollToId(e, l.href)}
                            className="px-4 min-h-[44px] flex items-center text-sm font-medium text-[var(--ink-2)] hover:text-[var(--accent)] rounded-full transition-colors duration-200"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="/app"
                        data-testid="nav-open-app-button"
                        className="ml-2 min-h-[44px] px-5 flex items-center text-sm font-semibold rounded-full bg-[var(--accent)] text-[#0A0908] hover:bg-[var(--accent-hover)] transition-colors duration-200"
                    >
                        Open IDeaSpace
                    </a>
                </div>

                <button
                    data-testid="nav-mobile-hamburger"
                    aria-label="Open menu"
                    aria-expanded={open}
                    onClick={() => setOpen(true)}
                    className="md:hidden w-11 h-11 grid place-items-center rounded-lg text-[var(--ink)] hover:bg-[var(--accent-soft)] transition-colors"
                >
                    <Menu size={22} />
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            data-testid="nav-mobile-backdrop"
                            className="fixed inset-0 bg-[#1B1209]/50 backdrop-blur-sm md:hidden"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                        />
                        <motion.aside
                            data-testid="nav-mobile-drawer"
                            className="fixed top-0 right-0 h-full w-[80%] max-w-xs bg-[var(--paper)] shadow-2xl md:hidden flex flex-col"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.35, ease: EASE }}
                        >
                            <div className="flex items-center justify-between px-5 h-16 border-b border-[var(--line)]">
                                <span className="font-display font-bold text-[var(--ink)]">IDeaSpace</span>
                                <button
                                    data-testid="nav-mobile-close"
                                    aria-label="Close menu"
                                    onClick={() => setOpen(false)}
                                    className="w-11 h-11 grid place-items-center rounded-lg hover:bg-[var(--accent-soft)] transition-colors"
                                >
                                    <X size={22} />
                                </button>
                            </div>
                            <div className="flex flex-col p-4 gap-1">
                                {LINKS.map((l, i) => (
                                    <motion.a
                                        key={l.href}
                                        href={l.href}
                                        data-testid={`nav-mobile-${l.testid}`}
                                        onClick={(e) => scrollToId(e, l.href, () => setOpen(false))}
                                        className="px-4 min-h-[48px] flex items-center text-base font-medium text-[var(--ink-2)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] rounded-xl transition-colors"
                                        initial={{ opacity: 0, x: 24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.08 + i * 0.06, duration: 0.35, ease: EASE }}
                                    >
                                        {l.label}
                                    </motion.a>
                                ))}
                                <motion.a
                                    href="/app"
                                    data-testid="nav-mobile-open-app-button"
                                    className="mt-3 min-h-[48px] px-4 flex items-center justify-center text-base font-semibold rounded-xl bg-[var(--accent)] text-[#0A0908]"
                                    initial={{ opacity: 0, x: 24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.36, duration: 0.35, ease: EASE }}
                                >
                                    Open IDeaSpace
                                </motion.a>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </header>
    );
}
